import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const ZONES = [1, 2, 3, 4, 5, 6, 7, 8];

/** Second shipping driver: 4-72 Exportafácil EMS (table price by zone + weight, hand-in at 4-72). */
const AdminEmsShipping = () => {
  const { toast } = useToast();
  const headers = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/shipping/config/ems`, { headers }).then((r) => setCfg(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { enabled, zones, delivery_days, package: pkg, markup_percent } = cfg;
      const r = await axios.put(`${API}/shipping/config/ems`, { enabled, zones, delivery_days, package: pkg, markup_percent }, { headers });
      setCfg(r.data);
      toast({ title: 'Saved', description: '4-72 EMS settings updated' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Save failed', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return null;
  const setZone = (code, z) => {
    const zones = { ...(cfg.zones || {}) };
    if (z) zones[code] = Number(z); else delete zones[code];
    setCfg({ ...cfg, zones });
  };
  const setDays = (zone, idx, v) => {
    const days = { ...(cfg.delivery_days || {}) };
    const cur = [...(days[String(zone)] || [6, 12])];
    cur[idx] = Number(v) || 0;
    days[String(zone)] = cur;
    setCfg({ ...cfg, delivery_days: days });
  };
  const setPkg = (k, v) => setCfg({ ...cfg, package: { ...cfg.package, [k]: Number(v) } });

  return (
    <Card data-testid="ems-settings">
      <CardHeader>
        <CardTitle>4-72 EMS (Exportafácil)</CardTitle>
        <CardDescription>
          Second shipping option for international orders, next to Envía. Price from 4-72&apos;s table by zone and
          chargeable weight (larger of real weight and L×W×H/5000), insurance included. No API: hand the package in at
          4-72 and type the tracking number in the order (the customer is e-mailed).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between border rounded-md p-3">
          <div>
            <Label>Offer 4-72 EMS at checkout</Label>
            <p className="text-sm text-gray-500">Only for countries with a zone below. Colombia never (it&apos;s an export service).</p>
          </div>
          <Switch data-testid="ems-enabled" checked={!!cfg.enabled} onCheckedChange={(on) => setCfg({ ...cfg, enabled: on })} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div><Label>Length cm</Label><Input type="number" value={cfg.package.length} onChange={(e) => setPkg('length', e.target.value)} /></div>
          <div><Label>Width cm</Label><Input type="number" value={cfg.package.width} onChange={(e) => setPkg('width', e.target.value)} /></div>
          <div><Label>Height cm</Label><Input type="number" value={cfg.package.height} onChange={(e) => setPkg('height', e.target.value)} /></div>
          <div><Label>Packaging kg</Label><Input type="number" step="0.05" value={cfg.package.base_weight} onChange={(e) => setPkg('base_weight', e.target.value)} /></div>
          <div><Label>Kg per item</Label><Input type="number" step="0.05" value={cfg.package.weight_per_item} onChange={(e) => setPkg('weight_per_item', e.target.value)} /></div>
          <div><Label>Markup %</Label><Input type="number" value={cfg.markup_percent} onChange={(e) => setCfg({ ...cfg, markup_percent: Number(e.target.value) })} /></div>
        </div>

        <div>
          <Label>Zone per country (from 4-72&apos;s zone list)</Label>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-500"><th className="py-1">Country</th><th>Zone</th><th>Example, 1 item</th></tr></thead>
              <tbody>
                {cfg.countries.map((c) => {
                  const ex = cfg.zones?.[c.code] ? cfg.example_one_item?.[c.code] : null;
                  return (
                    <tr key={c.code} className="border-t">
                      <td className="py-1">{c.code} · {c.name}</td>
                      <td>
                        <select className="border rounded px-2 py-1" value={cfg.zones?.[c.code] || ''} onChange={(e) => setZone(c.code, e.target.value)}>
                          <option value="">— not offered —</option>
                          {ZONES.map((z) => <option key={z} value={z}>Zone {z}</option>)}
                        </select>
                      </td>
                      <td className="text-gray-600">{ex ? `${ex.price} USD · ${ex.chargeable_kg} kg · ${ex.days[0]}–${ex.days[1]} days` : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-1">Examples refresh after saving.</p>
        </div>

        <div>
          <Label>Delivery time shown to customers (business days, door to door)</Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
            {ZONES.map((z) => {
              const d = cfg.delivery_days?.[String(z)] || [6, 12];
              return (
                <div key={z} className="flex items-center gap-1 text-sm">
                  <span className="w-14">Zone {z}</span>
                  <Input type="number" className="w-16" value={d[0]} onChange={(e) => setDays(z, 0, e.target.value)} />
                  <span>–</span>
                  <Input type="number" className="w-16" value={d[1]} onChange={(e) => setDays(z, 1, e.target.value)} />
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-[#be1f1a] hover:bg-[#a71b17]" data-testid="ems-save">
            {saving ? 'Saving…' : 'Save 4-72 EMS'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminEmsShipping;
